'use client';

// useViewerTimezone — the viewer's own device IANA zone (BL-596), resolved in a
// hydration-safe way. Returns `undefined` on the server and the first client
// render, then the real device zone after hydration. That ordering matters: the
// device zone is unknowable on the server, so emitting it during SSR would
// produce markup that disagrees with the client and trip a hydration mismatch.
// Paired with each app's tenant/house-zone reader, it drives the dual-zone
// secondary in `<EventTime>` — which therefore only appears post-hydration,
// never in the SSR HTML.
//
// SHARED (sharing.md § 1). Originally bidder-web-only; lifted here by BL-1258
// when the admin clerk console needed the same reader for its bid record. The
// logic is app-agnostic — no auth, no tenant, no routing — so it belongs beside
// `<EventTime>`, the component it feeds. `apps/bidder-web/src/hooks/
// useViewerTimezone.ts` is now a one-line re-export of this, so both apps run
// one implementation and bidder-web's existing `@/hooks/...` import path (and
// the suites that mock it) keep working.

import { useSyncExternalStore } from 'react';

/** Stable no-op subscribe — the device zone doesn't change during a session. */
const SUBSCRIBE_NOOP = (): (() => void) => () => {};

/** Read the device's IANA zone, defaulting to `UTC` if Intl can't resolve one. */
function readViewerZone(): string {
    try {
        return new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

/**
 * The viewer's device timezone (IANA), or `undefined` until after hydration.
 *
 * Server / first-paint snapshot is `undefined` so callers render the
 * auction-house zone alone; once hydrated this returns the real device zone and
 * the dual-zone secondary appears. Consumers pass the result straight into the
 * zone-aware formatters' `viewerZone` option.
 *
 * @returns the device IANA zone, or `undefined` before hydration.
 */
export function useViewerTimezone(): string | undefined {
    return useSyncExternalStore(SUBSCRIBE_NOOP, readViewerZone, () => undefined);
}
